// docs/44 the music director: one score at a time on the music bus, chosen by the moment. Each
// screen has a mood (content/audio/music.moods.json; the tracks are the generated banks
// `music.<mood>`); a scene may push a mood of its own over it while it is on screen (the intro by
// the World's tone, the named foe revealed, the face-off, a won or lost field). The track inside a
// mood is seeded by the World, so a party watching the cold open together hears the same piece; when
// it ends the next one in the mood follows. A change of mood crossfades. Cinematics duck the score
// under their words.
import { useEffect } from 'react';
import { create } from 'zustand';
import { musicNow, onMusicEnded, playMusic, setMusicDuck, sfxVariantCount, stopMusic, type SfxKey } from './index';
import { useRoute, type Screen } from '../stores/route';
import { useSession } from '../stores/session';
import { useEncounter } from '../encounter/session';
import { hash32 } from './mix';
import type { Tone } from '../story/genesis';

export type Mood = 'title' | 'wander' | 'haven' | 'wonder' | 'mystery' | 'dread' | 'tension' | 'battle' | 'triumph' | 'sorrow' | 'playful';

/** Each screen's own mood; a screen not listed keeps whatever is playing (settings over anything). */
const SCREEN_MOOD: Partial<Record<Screen, Mood>> = {
  title: 'title', online: 'title', gathering: 'haven', writing: 'mystery', intro: 'wonder', epilogue: 'triumph',
  world: 'wander', lobby: 'wander', localmap: 'wander', encounter: 'battle', spoils: 'triumph', aftermath: 'haven',
  roster: 'haven', village: 'haven', levelup: 'triumph', inventory: 'haven', ledger: 'haven', realm: 'wonder',
};

/** A World's intro by its tone. */
export const TONE_MOOD: Record<Tone, Mood> = { Grim: 'dread', Eerie: 'mystery', Mythic: 'wonder', Hopeful: 'wonder', Rollicking: 'playful' };

/** Encounter states and outcomes (fk_sim): Ended = 3; Victory 0, Surrendered 3 count as a won field. */
const ENC_ENDED = 3;
const WON = new Set([0, 3]);
const FADE_MS = 2600;
const DUCK = 0.45;

interface Pushed { id: number; mood?: Mood; duck?: boolean; /** docs/116: someone else (the inn's bard) has the music bus */ hold?: boolean }
interface MusicStore { pushed: Pushed[]; push(p: Pushed): void; drop(id: number): void }
const useMusic = create<MusicStore>()((set, get) => ({
  pushed: [],
  push(p) { set({ pushed: [...get().pushed.filter((x) => x.id !== p.id), p] }); },
  drop(id) { set({ pushed: get().pushed.filter((x) => x.id !== id) }); },
}));
let nextId = 1;

/** While mounted, the score plays `mood` (the latest scene to ask wins); `duck` lowers it under words. */
export function useMusicMood(mood: Mood | undefined, duck = false): void {
  useEffect(() => {
    const id = nextId++;
    useMusic.getState().push({ id, mood, duck });
    return () => useMusic.getState().drop(id);
  }, [mood, duck]);
}

/** docs/116 CI7: while mounted and `on`, the director lets the music bus be (the inn's bard plays on it). */
export function useMusicHold(on: boolean): void {
  useEffect(() => {
    if (!on) return;
    const id = nextId++;
    useMusic.getState().push({ id, hold: true });
    return () => useMusic.getState().drop(id);
  }, [on]);
}

/** The mood the moment asks for. */
export function moodFor(screen: Screen, pushed: readonly Pushed[], enc: { state: number; outcome?: number }): Mood | undefined {
  const top = [...pushed].reverse().find((p) => p.mood);
  if (top?.mood) return top.mood;
  if (screen === 'encounter' && enc.state === ENC_ENDED) return enc.outcome !== undefined && WON.has(enc.outcome) ? 'triumph' : 'sorrow';
  return SCREEN_MOOD[screen];
}

/** The track of `mood` for this World: the same first piece at every table, then the next. */
export function trackFor(mood: Mood, world: bigint | undefined, turn: number): number | undefined {
  const n = sfxVariantCount(`music.${mood}` as SfxKey);
  if (!n) return undefined;
  return (hash32(`${world ?? 0n}:${mood}`) + turn) % n;
}

/** Mount once (App): follows the route and the scenes' moods, and keeps a score playing. */
export function MusicDirector(): null {
  const screen = useRoute((s) => s.screen);
  const pushed = useMusic((s) => s.pushed);
  const world = useSession((s) => s.worldId);
  const state = useEncounter((s) => s.state);
  const outcome = useEncounter((s) => s.outcome);
  const mood = moodFor(screen, pushed, { state, outcome });
  const duck = pushed.some((p) => p.duck);
  const held = pushed.some((p) => p.hold);
  useEffect(() => {
    if (!mood || held) return;   // a screen with no mood of its own keeps the score that is playing
    let turn = 0;
    const play = () => {
      const variant = trackFor(mood, world, turn++);
      if (variant === undefined) { stopMusic(FADE_MS); return; }
      const key = `music.${mood}` as SfxKey, now = musicNow();
      // already playing this mood (the director remounted, or a scene asked for what plays): carry on
      if (turn === 1 && now?.key === key) return;
      playMusic(key, { variant, fadeMs: FADE_MS });
    };
    play();
    // the piece ended: the next one of the same mood
    return onMusicEnded(play);
  }, [mood, world, held]);
  useEffect(() => { setMusicDuck(duck ? DUCK : 0, 900); }, [duck]);
  return null;
}
